class DynamicHeadline extends HTMLElement {
  connectedCallback() {
    this.prefixes = this.parseList(this.dataset.prefix);
    this.suffixes = this.parseList(this.dataset.suffix);
    this.template = this.dataset.template || '';
    this.delay = (parseFloat(this.dataset.delay) || 3) * 1000;
    this.animation = this.dataset.animation || 'typewriter';
    this.charSpeed = 60;
    this.eraseSpeed = 40;
    this.fadeDuration = 400;

    this.prefixEl = this.querySelector('[data-role="prefix"]');
    this.suffixEl = this.querySelector('[data-role="suffix"]');
    this.prefixCursor = this.querySelector('[data-role="prefix-cursor"]');
    this.suffixCursor = this.querySelector('[data-role="suffix-cursor"]');

    this.prefixIndex = 0;
    this.suffixIndex = 0;
    this.running = true;

    this.run();
  }

  disconnectedCallback() {
    this.running = false;
  }

  parseList(raw) {
    if (!raw) return [''];
    return raw.split(',').map(s => s.trim()).filter(s => s.length > 0);
  }

  sleep(ms) {
    return new Promise(r => setTimeout(r, ms));
  }

  setCursorActive(cursor) {
    if (this.prefixCursor) {
      this.prefixCursor.classList.toggle('dynamic-headline__cursor--hidden', this.prefixCursor !== cursor);
    }
    if (this.suffixCursor) {
      this.suffixCursor.classList.toggle('dynamic-headline__cursor--hidden', this.suffixCursor !== cursor);
    }
  }

  showAllCursors() {
    if (this.prefixCursor) this.prefixCursor.classList.remove('dynamic-headline__cursor--hidden');
    if (this.suffixCursor) this.suffixCursor.classList.remove('dynamic-headline__cursor--hidden');
  }

  // --- Typewriter methods ---

  async typeText(el, text, cursor) {
    if (cursor) this.setCursorActive(cursor);
    for (let i = 0; i <= text.length && this.running; i++) {
      el.textContent = text.slice(0, i);
      await this.sleep(this.charSpeed);
    }
  }

  async eraseText(el, cursor) {
    if (cursor) this.setCursorActive(cursor);
    const text = el.textContent;
    for (let i = text.length; i >= 0 && this.running; i--) {
      el.textContent = text.slice(0, i);
      await this.sleep(this.eraseSpeed);
    }
  }

  // --- Vertical fade methods ---

  async fadeOut(el) {
    el.classList.remove('fade-in-up', 'fade-in-from-below');
    el.classList.add('fade-out-up');
    await this.sleep(this.fadeDuration);
  }

  async fadeIn(el, text) {
    el.classList.remove('fade-out-up');
    el.classList.add('fade-in-from-below');
    el.textContent = text;
    // Force reflow so the browser registers the starting position
    el.offsetHeight;
    el.classList.remove('fade-in-from-below');
    el.classList.add('fade-in-up');
    await this.sleep(this.fadeDuration);
  }

  // --- Run loops ---

  async runTypewriter() {
    if (this.prefixEl && this.prefixes.length) {
      await this.typeText(this.prefixEl, this.prefixes[0], this.prefixCursor);
    }
    if (this.suffixEl && this.suffixes.length) {
      await this.typeText(this.suffixEl, this.suffixes[0], this.suffixCursor);
    }

    this.showAllCursors();
    await this.sleep(this.delay);

    while (this.running) {
      const prefixChanging = this.prefixes.length > 1;
      const suffixChanging = this.suffixes.length > 1;

      if (suffixChanging && this.suffixEl) {
        await this.eraseText(this.suffixEl, this.suffixCursor);
      }
      if (prefixChanging && this.prefixEl) {
        await this.eraseText(this.prefixEl, this.prefixCursor);
      }

      if (prefixChanging) this.prefixIndex = (this.prefixIndex + 1) % this.prefixes.length;
      if (suffixChanging) this.suffixIndex = (this.suffixIndex + 1) % this.suffixes.length;

      if (prefixChanging && this.prefixEl) {
        await this.typeText(this.prefixEl, this.prefixes[this.prefixIndex], this.prefixCursor);
      }
      if (suffixChanging && this.suffixEl) {
        await this.typeText(this.suffixEl, this.suffixes[this.suffixIndex], this.suffixCursor);
      }

      this.showAllCursors();
      await this.sleep(this.delay);
    }
  }

  async runVerticalFade() {
    if (this.prefixEl) {
      this.prefixEl.textContent = this.prefixes[0];
      this.prefixEl.classList.add('fade-in-up');
    }
    if (this.suffixEl) {
      this.suffixEl.textContent = this.suffixes[0];
      this.suffixEl.classList.add('fade-in-up');
    }

    this.showAllCursors();
    await this.sleep(this.delay);

    while (this.running) {
      const prefixChanging = this.prefixes.length > 1;
      const suffixChanging = this.suffixes.length > 1;

      const fadeOuts = [];
      if (prefixChanging && this.prefixEl) fadeOuts.push(this.fadeOut(this.prefixEl));
      if (suffixChanging && this.suffixEl) fadeOuts.push(this.fadeOut(this.suffixEl));
      if (fadeOuts.length) await Promise.all(fadeOuts);

      if (prefixChanging) this.prefixIndex = (this.prefixIndex + 1) % this.prefixes.length;
      if (suffixChanging) this.suffixIndex = (this.suffixIndex + 1) % this.suffixes.length;

      const fadeIns = [];
      if (prefixChanging && this.prefixEl) fadeIns.push(this.fadeIn(this.prefixEl, this.prefixes[this.prefixIndex]));
      if (suffixChanging && this.suffixEl) fadeIns.push(this.fadeIn(this.suffixEl, this.suffixes[this.suffixIndex]));
      if (fadeIns.length) await Promise.all(fadeIns);

      this.showAllCursors();
      await this.sleep(this.delay);
    }
  }

  async run() {
    if (this.animation === 'vertical_fade') {
      await this.runVerticalFade();
    } else {
      await this.runTypewriter();
    }
  }
}

if (!customElements.get('dynamic-headline')) {
  customElements.define('dynamic-headline', DynamicHeadline);
}
